// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2024 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

// TODO(#4108): Consider and apply refactoring `UsTnAnnualReclassificationReview...` and `UsTnCustodyLevelDowngrade...` files to remove duplicated logic.
import { zip } from "lodash";
import { observer } from "mobx-react-lite";
import React, { useContext } from "react";
import styled from "styled-components";

import { AssessmentQuestionSpec } from "~datatypes";

import { UsTnSharedReclassificationDraftData } from "../../../../WorkflowsStore";
import { UsTnReclassificationReviewForm } from "../../../../WorkflowsStore/Opportunity/Forms/UsTnReclassificationReviewForm";
import DOCXFormTextArea from "../../DOCXFormTextArea";
import { FormViewerContext } from "../../FormViewer";
import { useOpportunityFormContext } from "../../OpportunityFormContext";
import { PrintablePage } from "../../styles";
import { ScoredAssessmentQuestion } from "../common/ScoredAssessmentQuestion";
import SealPng from "../common/Seal.png";
import {
  AssessmentQuestionNumber,
  assessmentQuestionNumbers,
  assessmentQuestions,
} from "./assessmentQuestions";
import AssessmentScore from "./AssessmentScore";
import CoverSheet from "./CoverSheet";
import FormInput from "./FormInput";
import HeaderFields from "./HeaderFields";
import HearingNotice from "./HearingNotice";
import { FormContainer, Label } from "./styles";

const Header = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  text-align: center;
  margin: 1em;
`;

const Seal = styled.img.attrs({ src: SealPng, alt: "TN Seal" })`
  height: 3rem;
  width: 3rem;
  margin-right: 1rem;
`;

const ClassificationCustodyAssessment: React.FC = () => {
  const formViewerContext = useContext(FormViewerContext);
  const form = useOpportunityFormContext() as UsTnReclassificationReviewForm;

  const numberedQuestions = zip(
    assessmentQuestions,
    assessmentQuestionNumbers,
  ) as [AssessmentQuestionSpec, AssessmentQuestionNumber][];

  const scheduleA = numberedQuestions.slice(0, 4);
  const scheduleB = numberedQuestions.slice(4);
  const handleSupportingText = (questionNumber: AssessmentQuestionNumber) => {
    if ([1, 2, 6, 7, 8].includes(questionNumber))
      return "**This score has been pre-filled based on data from eTOMIS and logic calculated by Recidiviz. If incorrect, please reach out to feedback@recidiviz.org to notify us.";
    else if ([3, 4, 5, 9].includes(questionNumber))
      return "**This score has been pre-filled directly from eTOMIS from the resident's previous Classification. If incorrect, please correct in eTOMIS when updating this resident's next Classification.";
    else return undefined;
  };

  const {
    scheduleAScore,
    scheduleAText,
    totalScore,
    totalText,
    scheduleBSkipped,
  } = form.derivedData;

  return (
    <>
      <CoverSheet />
      <PrintablePage stretchable>
        <FormContainer {...formViewerContext}>
          <Header>
            <Seal />
            <div>
              TENNESSEE DEPARTMENT OF CORRECTION <br />
              CLASSIFICATION CUSTODY ASSESSMENT <br />
              <Label>
                INSTITUTION:{" "}
                <FormInput
                  style={{ borderBottom: "0.5px solid black" }}
                  name="institutionName"
                />
              </Label>
            </div>
          </Header>
          <HeaderFields />
          <div>
            {scheduleA.map(([q, i]) => (
              <ScoredAssessmentQuestion
                questionSpec={q}
                questionNumber={i}
                key={i}
                supportingText={handleSupportingText(i)}
              >
                <DOCXFormTextArea<UsTnSharedReclassificationDraftData>
                  name={`q${i}Note`}
                  placeholder="Add Note"
                  style={{ width: "100%", fontStyle: "italic" }}
                />
              </ScoredAssessmentQuestion>
            ))}
          </div>
          <AssessmentScore
            score={scheduleAScore}
            scoreText={scheduleAText}
            title="SCHEDULE A SCALE (SUM OF ITEMS 1 THROUGH 4)"
            levels={[
              { text: "Close", min: 10, max: 14 },
              { text: "Maximum", min: 15 },
              { text: "Complete Schedule B", max: 9 },
            ]}
          />
        </FormContainer>
      </PrintablePage>
      <PrintablePage stretchable>
        <FormContainer {...formViewerContext}>
          <div>
            {scheduleB.map(([q, i]) => (
              <ScoredAssessmentQuestion
                questionSpec={q}
                questionNumber={i}
                key={i}
                disabled={scheduleBSkipped}
                supportingText={handleSupportingText(i)}
              >
                <DOCXFormTextArea<UsTnSharedReclassificationDraftData>
                  name={`q${i}Note`}
                  placeholder="Add Note"
                  style={{ width: "100%", fontStyle: "italic" }}
                />
              </ScoredAssessmentQuestion>
            ))}
          </div>
          <AssessmentScore
            title="CUSTODY LEVEL SCALE FOR TOTAL A+B (CAF SCORE)"
            score={totalScore}
            scoreText={totalText}
            levels={[
              { text: "Close", min: 17 },
              { text: "Medium", min: 7, max: 16 },
              { text: "Minimum", max: 6 },
            ]}
          />
        </FormContainer>
      </PrintablePage>
      <HearingNotice />
    </>
  );
};

export default observer(ClassificationCustodyAssessment);
